import React, { useEffect, useState } from 'react';

import { DashboardEditorProps } from 'components/ComponentType';
import { DatasetMultiSelect } from 'components/basic/dropdowns/dataset/DatasetMultiSelect';
import TimeUnitDropdown from 'components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown';
import {
  defaultDatasetLineChartConfig,
  defaultLoadConf,
} from 'components/dashboard/data-input-output-table/defaultConfigs';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DropdownChangeParams } from 'primereact/dropdown';
import { InputNumber, InputNumberValueChangeParams } from 'primereact/inputnumber';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';
import { DatasetChartProps, DatasetLoadingConf } from '../interfaces';
import LineEditor from './line-editor/LineEditor';

const DefaultDatasetLoadingConf: DatasetLoadingConf = {
	lead: 0,
	lag: 6,
	lagUnit: 'HOURS',
	leadUnit: 'HOURS',
	alignUnit: null,
};

function DatasetLineChartEditor({ config, onConfigChange }: DashboardEditorProps<DatasetChartProps>) {
    let conf = config ?? defaultDatasetLineChartConfig;

    if (Object.keys(conf).length === 0) {
        conf = defaultDatasetLineChartConfig;
    }

    const [currentConfig, setCurrentConfig] = useState(conf);
    const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
    const [lineEditorDialogVisible, setLineEditorDialogVisibility] = useState<boolean>(false);

    useEffect(() => {
        const initialSelectedDatasets = currentConfig.datasets?.map((dataset) => dataset.identifier);
        setSelectedDatasets(initialSelectedDatasets ?? []);
    }, []);

    const handleConfigChange = (e: InputNumberValueChangeParams | InputSwitchChangeParams): void => {
        setCurrentConfig(prev => ({...prev, [e.target.id]: e.target.value}));
    };

    const handleDatasetChange = (selectedIdentifiers: string[]): void => {
      const selectedDatasetsArray = selectedIdentifiers.map(identifier => {
        const oldDataset = currentConfig.datasets
          ? currentConfig.datasets?.find(dataset => dataset.identifier === identifier)
          : undefined;
        if (oldDataset) {
          return oldDataset;
        }
        return {
          identifier,
        };
      });

      setCurrentConfig(prev => ({ ...prev, datasets: selectedDatasetsArray }));
      setSelectedDatasets(selectedIdentifiers);
    };

    const handleLoadConf = (e: InputSwitchChangeParams): void => {
        const tempConfig = structuredClone(currentConfig);
        if (e.value) {
            tempConfig.loadConf = defaultLoadConf;
        } else {
            delete tempConfig.loadConf;
        }
        setCurrentConfig(tempConfig);
    };

    const handleLoadConfChange = (e: InputNumberValueChangeParams | DropdownChangeParams) => {
        const key = e.target.id as keyof DatasetLoadingConf;
        let { value } = e.target;
        if (isNaN(value)) value = value.toString().toUpperCase();
        setCurrentConfig(prev => ({
            ...prev,
            loadConf: prev.loadConf
                ? { ...prev.loadConf, [key]: value }
                : { ...DefaultDatasetLoadingConf, [key]: value },
        }));
    };

    const handleEditLinesClick = () => {
        setLineEditorDialogVisibility(true);
    };

    useEffect(() => {
        if(currentConfig.identifiers) {
            delete currentConfig.identifiers
        }
        onConfigChange(currentConfig);
    }, [currentConfig]);

    return (
        <div className='grid p-fluid'>
            <div className='col-12 flex flex-column mb-3'>
                <label htmlFor='identifiers'>Datasets</label>
                <div className='flex gap-2'>
                    <DatasetMultiSelect value={selectedDatasets} onChange={handleDatasetChange} />
                    {selectedDatasets.length > 0 && (
                        <>
                            <Button
                                icon='pi pi-pencil'
                                type='button'
                                className='p-button-secondary'
                                tooltip='Edit Lines'
                                onClick={handleEditLinesClick}
                            />
                            <Dialog
                                onHide={() => setLineEditorDialogVisibility(false)}
                                visible={lineEditorDialogVisible}
                                header='Edit Line style'
                                style={{ width: 'min(100vw, 1000px)', height: '400x' }}
                            >
                                <LineEditor
                                    currentConfig={currentConfig}
                                    setCurrentConfig={setCurrentConfig}
                                />
                            </Dialog>
                        </>
                    )}
                </div>
            </div>
            
            <div className='col-2 flex flex-column mb-3'>
                <label htmlFor='updateFrequency'>Update Frequency</label>
                <div className='p-inputgroup w-full'>
                    <InputNumber
                        id='updateFrequency'
                        value={currentConfig.updateFrequency as number}
                        onValueChange={handleConfigChange}
                    />
                    <span className='p-inputgroup-addon'>s</span>
                </div>
            </div>
            <div className='col-2 flex flex-column mb-full'>
                <label htmlFor='timeAxisStepSeconds'>Time Axis Step</label>
                <div className='p-inputgroup w-full'>
                    <InputNumber
                        id='timeAxisStepSeconds'
                        value={currentConfig.timeAxisStepSeconds as number}
                        onValueChange={handleConfigChange}
                    />
                    <span className='p-inputgroup-addon'>s</span>
                </div>
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='showDateSelection'>Show Date Selection</label>
                <InputSwitch
                    id='showDateSelection'
                    checked={currentConfig.showDateSelection}
                    onChange={handleConfigChange}
                />
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='hideRefresh'>Hide Refresh</label>
                <InputSwitch id='hideRefresh' checked={currentConfig.hideRefresh} onChange={handleConfigChange} />
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='toggleTracesGrouping'>Group traces</label>
                <InputSwitch
                    id='toggleTracesGrouping'
                    checked={currentConfig.toggleTracesGrouping}
                    onChange={handleConfigChange}
                />
            </div>
            <div className='gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='loadConf'>Load Conf</label>
                <InputSwitch id='loadConf' checked={currentConfig.loadConf !== undefined} onChange={handleLoadConf} />
            </div>
            {currentConfig.loadConf !== undefined && (
                <>
                    <div className='col-4 gap-2 flex flex-column justify-content-center align-items-center'>
                        <label htmlFor='lag'>Lag</label>
                        <div className='p-inputgroup'>
                            <InputNumber
                                id='lag'
                                value={currentConfig.loadConf.lag as number}
                                onValueChange={handleLoadConfChange}
                            />
                            <TimeUnitDropdown
                                id='lagUnit'
                                className='p-inputgroup-addon p-0'
                                value={currentConfig.loadConf.lagUnit}
                                onChange={handleLoadConfChange}
                            />
                        </div>
                    </div>
                    <div className='col-4 gap-2 flex flex-column justify-content-center align-items-center'>
                        <label htmlFor='lead'>Lead</label>
                        <div className='p-inputgroup'>
                            <InputNumber
                                id='lead'
                                value={currentConfig.loadConf.lead as number}
                                onValueChange={handleLoadConfChange}
                            />
                            <TimeUnitDropdown
                                id='leadUnit'
                                className='p-inputgroup-addon p-0'
                                value={currentConfig.loadConf.leadUnit}
                                onChange={handleLoadConfChange}
                            />
                        </div>
                    </div>
                    <div className='col-3 gap-2 flex flex-column justify-content-center align-items-center'>
                        <label htmlFor='alignUnit'>Align Unit</label>
                        <TimeUnitDropdown
                            id='alignUnit'
                            className='w-full'
                            value={currentConfig.loadConf.alignUnit ? currentConfig.loadConf.alignUnit : ''}
                            onChange={handleLoadConfChange}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export default DatasetLineChartEditor;